import styled from 'styled-components'
import Image from 'next/legacy/image'
import Link from 'next/link'
import { Typography } from 'common/typography'
import { SecondaryButton, TransparentButton } from 'common/button'
import ContentRenderer from 'content-renderer'
import { useRouter } from 'next/router'
import useSession from 'querys/session'
import LearningPlanTag from './learning-plan-tag'

const LearningPlanCourseItemStyled = styled.li`
  position: relative;
  list-style: none;
  ${({ $isActive }) => $isActive && (
    `
      position: relative;
      z-index: 3;
    `
  )}
  .course-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .is-free {
    font: var(--smallBold);
    color: var(--black);
    background: #FFF858;
    padding-inline: 0.125rem;
    border-radius: 2px;
  }
  .learning-path-tooltip {
    filter: drop-shadow(0px 0px 20px #6170d850);
    box-sizing: border-box;
    display: flex;
    bottom: 0;
    max-inline-size: 328px;
    position: absolute;
    flex-direction: column;
    gap: 8px;
    background: var(--neutral90);
    border-radius: 24px;
    padding: 24px;
    z-index: 2;
    color: var(--white);
    z-index: 4;
    visibility: hidden;
    opacity: 0;
    &:after {
      content: "";
      position: absolute;
      display: block;
      width: 12px;
      height: 8px;
      z-index: 2;
      top: -6px;
      left: calc(50% - 6px);
      background: var(--neutral90);
      clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    }
    p {
      margin: 0;
    }
    &-spacer {
      height: 30px;
      position: absolute;
      left: 0;
      right: 0;
      bottom: 100%;
    }
  }

  .turquoise {
    color: var(--turquoise);
  }
  .course-summary p {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin: 0;
  }
  &:last-of-type .learning-path-tooltip {
    top: initial;
    bottom: 125%;
    &:after {
      top: initial;
      bottom: -6px;
      transform: rotate(180deg);
    }
  }
  .certificate-link {
    color: var(--turquoise);
    text-decoration: underline;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
  }
  .badge-container {
    display: flex;
    position: relative;
  }
  .badge {
    transform: rotateX(25deg) translateZ(-14px) translateY(-10px);
    box-shadow: 0 6px 0 #20203a;
    flex: 1;
    background: #3b3f56;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    box-sizing: border-box;
    padding: .25rem;
    z-index: 1;
    cursor: pointer;
    transition: .3s filter, .2s box-shadow;
    ${({ commingSoon }) => commingSoon && (
    `
        cursor: not-allowed;
        filter: grayscale(1);
      `
  )}
    img {
      filter: drop-shadow(2px 2px 2px rgba(0,0,0,.7));
    }
    ${({ completed }) => (completed ? `
      box-shadow: 0 6px 0 #20203a, 0 6px 15px #10FEFE;
    ` : null)}
    &:hover {
      box-shadow: 0 6px 0 #20203a, 0 6px 15px #10FEFE;
    }
  }
  @media (min-width: 1024px) {
    .learning-path-tooltip {
      bottom: initial;
      top: calc(100% + .5rem);
      right: 0;
      left: 0;
      min-inline-size: 380px;
      left: 50%;
      transform: translateX(-50%) scale(.1);
      transform-origin: top;
      transition: .3s transform;
      ${({ $isActive }) => $isActive && (`
        transform: translateX(-50%) scale(1);
        opacity: 1;
        visibility: visible;
      `)}
    }
    .badge-container {
      width: 70px;
      height: 70px;
      padding: .5rem;
      flex-shrink: 0;
    }
    .badge {
      &:active {
        box-shadow: none;
        position: relative;
        cursor: pointer;
        ${({ commingSoon }) => commingSoon && 'cursor: not-allowed;'}
        top: 4px;
      }
      span {
        vertical-align: middle;
      }
    }
  }
`

function LearningPlanCourseItem({
  title,
  slug,
  summary,
  badge,
  badge_state,
  last_view_lesson,
  course_progress,
  is_free,
  is_workshop,
  has_access,
  setCourse,
  featured_video,
  certificate,
  project_status,
  itemActive,
  setItemActive,
  status,
}) {
  const { progress, completed, total } = course_progress || {}
  const router = useRouter()
  const course = {
    title, slug, summary, featured_video,
  }
  const isActive = itemActive === course.slug
  const { token } = useSession()

  function handleClick() {
    if (status === 'COMING_SOON') return
    setCourse(course)
    if (isActive) {
      setItemActive('')
    } else {
      setItemActive(course.slug)
    }
  }
  function handleLinkClick() {
    const isMobile = window.matchMedia('(max-width: 1023px)').matches
    if (status === 'COMING_SOON' || !isMobile) return
    if (token) {
      router.push(`/aprender/${course.slug}`)
    } else {
      router.push(`/cursos/${course.slug}`)
    }
  }
  return (
    <LearningPlanCourseItemStyled
      onClick={handleLinkClick}
      $isActive={isActive}
      completed={progress === 100}
      commingSoon={status === 'COMING_SOON'}
      className="learning-plan-step"
    >
      <div className="course-item">
        <div className="badge-container">
          <div
            className="badge"
            onClick={handleClick}
            role="button"
            aria-hidden="true"
          >
            <Image
              src={badge_state?.image || badge?.image}
              width="45px"
              height="45px"

              alt={`Medalla del curso de ${title}`}
            />
          </div>
          <div className="learning-path-tooltip">
            <div className="learning-path-tooltip-spacer" />
            <Typography font="regularBold">
              {last_view_lesson?.title || title}
            </Typography>
            {completed ? (
              <Typography font="p2Regular">
                Clase
                {' '}
                <span className="turquoise">
                  {completed}
                  {' de '}
                  {total}
                </span>
              </Typography>
            ) : (
              <div className="course-summary">
                <ContentRenderer color="var(--white)" chunks={summary.blocks.slice(0, 1)} font="p2Regular" />
              </div>
            )}
            {last_view_lesson?.title ? (
              <Link href={`/aprender/${slug}/${last_view_lesson.slug}`} passHref>
                <SecondaryButton fullWidth>
                  Continuar curso
                </SecondaryButton>
              </Link>
            ) : (
              <Link href={has_access ? `/aprender/${slug}` : `/cursos/${slug}`} passHref>
                <SecondaryButton>
                  Iniciar curso
                </SecondaryButton>
              </Link>
            )}
            {/* {project_status === 'PENDING' ? (

            ) : null} */}
            {/* TODO: Has project started
              <OutlineButton>
                Añadir proyecto
              </OutlineButton>
            */}
          </div>
        </div>
        <div className="item-title">
          <LearningPlanTag comingSoon={status === 'COMING_SOON'} free={is_free} workshop={is_workshop} />
          <Typography font="regular" as="h2">
            {title}
            {progress > 0 ? (
              <b className="turquoise">
                {' '}
                {progress}
                %
              </b>
            ) : null}
            {' '}
          </Typography>
          {progress === 100 && certificate ? (
            <TransparentButton className="certificate-link" as="a" href={`/certificados/${certificate}`}>
              Ver certificado
            </TransparentButton>
          ) : null}
        </div>
      </div>

    </LearningPlanCourseItemStyled>
  )
}

export default LearningPlanCourseItem
